<!-- eslint-disable vue/html-self-closing -->
<!-- eslint-disable vue/no-v-html -->
<template>
  <MarketSelection
    v-if="store.selectedMarket === null && oneRosterSearch === ''"
    :page-title="pageTitle" />

  <l-stack-v3
    v-if="store.selectedMarket !== null"
    :class="$style['ua-school-selection-search']"
    align-x="center"
    gap="sm">
    <h1
      :class="testClass('page-title')">
      {{ pageTitle }}
    </h1>
    <p :class="$style['ua-school-selection-search__description']">
      Search by school, district, or location.
    </p>
    <CurrentSchoolsList
      v-if="showCurrentSchools"
      :school-list="schoolList" />
    <UaBigSelect
      ref="schoolSelectionRef"
      input-id="school_search_unique_school_name"
      label="School Name"
      style="width: 100%"
      :list-data="store.searchResultSchools"
      :result-messages="store.searchResultsMessages"
      :search-key="store.key"
      name="school_search[unique_school_name]"
      theme="m4"
      :size="showCurrentSchools ? 'sm' : 'md'"
      test-selector="school-search-unique-school-name"
      @big_select_input_change="handleSchoolSearch"
      @big_select_option_change="handleSchoolSelected" />
    <div ref="formElmsRef" aria-hidden="true" v-html="formItems.hiddenInputs" />
    <SchoolNotListedModal
      v-if="store.selectedMarket !== null" />
  </l-stack-v3>
  <div :class="$style['school-selection-buttons']">
    <a
    v-if="cancelPath"
    :href="cancelPath"
    class="c-button-v3"
    :class="cancelButtonClasses">
      {{ cancelBtnLabel }}
    </a>
    <button
      v-if="store.selectedMarket !== null"
      class="c-button-v3  c-button-v3--primary"
      :class="nextButtonClasses"
      type="submit"
      :disabled="!store.isOptionSelected"
      @click="handleAddSchool()">
      Next
    </button>
  </div>
  <form
    v-if="store.selectedMarket !== null"
    id="school_search_form"
    accept-charset="UTF-8"
    action="/register/school"
    :class="[
      'c-form',
      $style['c-form--school-selection'],
      'l-pos-context'
    ]"
    method="post"
    aria-labelledby="a11y_add_school_heading_id">
  </form>
  <form method="POST" :action="searchFormAction" :class="$style['add-school-form']">
    <input name="commit" value="select" />
    <input name="school_search[school_id]" :value="store.selectedSchoolId" />
    <input type="hidden" name="authenticity_token" :value="authToken">
    <input ref="addSchoolSubmitRef" type="submit" />
  </form>
</template>

<script setup>
  import { computed, ref, useCssModule } from 'vue';
  import { testClass } from 'music';
  import CurrentSchoolsList from '../components/CurrentSchoolsList.vue'
  import MarketSelection from '../components/MarketSelection.vue'
  import SchoolNotListedModal from '../components/SchoolNotListedModal.vue'
  import UaBigSelect  from '~/src/shared_ui/vue_components/big_select/UaBigSelect.vue'
  import useSchoolSearchStore from '../stores/use_school_search_store.js'
  import searchSchools from '../lib/search_schools'
  import { default as authToken } from '~/src/shared/lib/auth_token.js'
  const props = defineProps({
    pageTitle: {
      type: String,
      required: true,
    },
    cancelBtnLabel: {
      type: String,
      default: 'Skip',
    },
    cancelPath: {
      type: String,
      default: undefined,
    },
    currentSchoolsList: {
      type: String,
      default: '[]',
    },
    addSchoolAction: {
      type: String,
      default: '',
    },
    returnTo: {
      type: String,
      default: '',
    },
    oneRosterSearch: {
      type: String,
      default: '',
    },
    districtSearch: {
      type: String,
      default: '',
    },
    excludeInternalSchools: {
      type: String,
      default: '',
    },
    searchFormAction: {
      type: String,
      default: ''
    }
  });

  function populateFormItems() {
    const formItemsElm = {
      'hiddenInputs': 'school_find_form_hidden_inputs',
      'currentSchoolsList': 'current_schools_list'
    };

    return (
      Object.keys(formItemsElm).reduce(
        (memo, key) => {
          memo[key] = document.getElementById(formItemsElm[key])?.innerHTML;
          return memo;
        },
        {}
      )
    );
  }

  const formItems = populateFormItems();

  const schoolSelectionRef = ref(null);
  const formElmsRef = ref(null);
  const addSchoolSubmitRef = ref(null);
  const store = useSchoolSearchStore();
  const $style = useCssModule();

  const searchSchoolByName = async(schoolName) => {
    const options = {
      'search_string': schoolName,
      'district_search': props.districtSearch,
      'one_roster_search': props.oneRosterSearch,
      'exclude_internal_schools': props.excludeInternalSchools,
      'endpoint': 'school/search',
      'selected_market': store.selectedMarket,
    }

    const results = await searchSchools(options);
    return results;
  }

  /**
   * 1. Resets the store's search state
   * 2. Calls searchSchoolsByName, which queries the server for schools by the search string.
   * @param {String} school_name - the search term to send to the server.
   */

  const handleSchoolSearch = async(school_name) => {
    store.resetSearchState();
    store.setSchoolSearchResults(await searchSchoolByName(school_name));
  }

  /**
   * Handles the big_select_option_change event emitted from UaBigSelect when
   * a user clicks on a school name. Updates the store with the selected school.
   * @param {Event Object} - the event object emitted  with big_select_option_change.
   */

  const handleSchoolSelected = (selected_school) => {
    store.storeSelectedSchool(selected_school);
  }

  const handleAddSchool = () => {
    addSchoolSubmitRef.value.click();
  };

  const schoolList = JSON.parse(props.currentSchoolsList);

  const showCurrentSchools = computed(() => {
    return store.selectedMarket !== null && schoolList.length > 0
  });

  const cancelButtonClasses = computed(() => {
    return [
      store.selectedMarket !== null 
        ? `c-button-v3--tertiary  ${$style['school-selection-buttons__width']}`
        : `c-button-v3--primary-clear  ${$style['ua-school-selection-search__btn-skip']}`,
      testClass('skip-school-selection')
    ];
  })

  const nextButtonClasses = computed(() => {
    return [
      $style['school-selection-buttons__width'],
      testClass('submit-button')
    ];
  })

  if (props.oneRosterSearch !== '') {
    store.selectedMarket = '';
  }

</script>

<style lang="scss" module>
  @use 'music/app/styles/library/base' as * ;

  .c-form--school-selection {
    width: 100%;
  }

  .ua-school-selection-search {
    margin: auto;
    padding: 0 1rem;

    &__description {
      color: var(--music-role-text, #1D1D1D);
      text-align: center;
      margin-bottom: 1rem;
    }

    &__btn-skip {
      margin: 1rem auto;
      width: 100%;
      text-decoration: none;
    }
  }

  .add-school-form {
    display: none;
  }

  .school-selection-buttons {
    display: flex;
    gap: rpx(20);
    justify-content: center;
    margin-top: rpx(16);
    width: 100%;

    &__width {
      width: rpx(140);
    }
  }
</style>
